/**
 * @fileoverview Maps report option ids to filter panel components and props.
 * @module pages/reports/filterPanels/reportFilterPanelRegistry
 *
 * Each resolver returns `{ Component, panelProps }` so ReportFilterPanels can
 * render the correct export filters without branching on every option id.
 */
import { REPORT_OPTION } from "../../../utils/reportConstants.js";
import TablesReportPanel from "./TablesReportPanel.jsx";
import LoginReportPanel from "./LoginReportPanel.jsx";
import BuyerReportPanel from "./BuyerReportPanel.jsx";
import B2bAttendanceReportPanel from "./B2bAttendanceReportPanel.jsx";
import InvoiceReportPanel from "./InvoiceReportPanel.jsx";
import YearBusinessEventPanel from "./YearBusinessEventPanel.jsx";
import YearEventTypePanel from "./YearEventTypePanel.jsx";
import YearB2bEventPanel from "./YearB2bEventPanel.jsx";

/** Wraps year + business-event panels that share the same base layout. */
const resolveYearBusinessEventPanel = (props, panelProps) => ({
  Component: YearBusinessEventPanel,
  panelProps: { ...props, ...panelProps },
});

/** Wraps year + multi-select event-type panels (with/without virtual types). */
const resolveYearEventTypePanel = (props, includeVirtualEventTypes) => ({
  Component: YearEventTypePanel,
  panelProps: { ...props, includeVirtualEventTypes },
});

/** @type {Map<number, (props: Object) => { Component: Function, panelProps: Object }>} */
const REPORT_FILTER_PANEL_RESOLVERS = new Map([
  [REPORT_OPTION.TABLES, (props) => ({ Component: TablesReportPanel, panelProps: props })],
  [
    REPORT_OPTION.AUTHENTICATION_LOGS,
    (props) => ({ Component: LoginReportPanel, panelProps: props }),
  ],
  [REPORT_OPTION.BUYER_LOGS, (props) => ({ Component: BuyerReportPanel, panelProps: props })],
  [
    REPORT_OPTION.B2B_ATTENDANCE,
    (props) => ({ Component: B2bAttendanceReportPanel, panelProps: props }),
  ],
  [
    REPORT_OPTION.MEMBER_INVOICE,
    (props) => ({ Component: InvoiceReportPanel, panelProps: props }),
  ],
  [
    REPORT_OPTION.NON_MEMBER_INVOICE,
    (props) => ({ Component: InvoiceReportPanel, panelProps: props }),
  ],
  [
    REPORT_OPTION.GENERAL_APPOINTMENTS,
    (props) =>
      resolveYearBusinessEventPanel(props, {
        eventDisabled: !props.selectedReportOption,
      }),
  ],
  [
    REPORT_OPTION.APPOINTMENT_DETAILS,
    (props) =>
      resolveYearBusinessEventPanel(props, {
        eventDisabled: !props.selectedYear,
      }),
  ],
  [
    REPORT_OPTION.ACCUMULATED_CONNECTIONS,
    (props) => resolveYearEventTypePanel(props, true),
  ],
  [
    REPORT_OPTION.REQUIREMENTS,
    (props) =>
      resolveYearBusinessEventPanel(props, {
        eventDisabled: !props.selectedYear,
      }),
  ],
  [
    REPORT_OPTION.EVENT_ATTENDANCES,
    (props) => resolveYearEventTypePanel(props, false),
  ],
  [REPORT_OPTION.GENERAL_B2B, (props) => ({ Component: YearB2bEventPanel, panelProps: props })],
  [
    REPORT_OPTION.VIRTUAL_REGISTRATION,
    (props) =>
      resolveYearBusinessEventPanel(props, {
        eventClearable: true,
        eventDisabled: !props.selectedYear,
      }),
  ],
]);

/** Returns the panel component and props for the selected report option. */
export const resolveReportFilterPanel = (optionValue, props) => {
  const resolver = REPORT_FILTER_PANEL_RESOLVERS.get(optionValue);
  return resolver ? resolver(props) : null;
};
